"use client"

import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Camera, Phone, User, MapPin, AtSign, Edit3, Check, Sparkles, Zap, UserCircle } from "lucide-react"
import { authActions } from "../../../redux/actions"
import WrapUpP2p from "../p2p/layouts/WrapUpP2p"
import { guessAdminRoute, notify } from "../../../utility/Helper1"
import { userSelect } from "../../../redux/selectors"

const SettingsProfile = () => {
    const userData = useSelector(userSelect)
    const dispatch = useDispatch()

    // State management
    const [uploadingAvatar, setUploadingAvatar] = useState(false)
    const [phoneUpdate, setPhoneUpdate] = useState(false)
    const [phoneNumber, setPhoneNumber] = useState(userData?.phone || "")
    const [accountUpdate, setAccountUpdate] = useState(false)
    const [activeSection, setActiveSection] = useState("all")
    const [profileData, setProfileData] = useState({
        firstname: userData?.firstname || "",
        middlename: userData?.middlename || "",
        surname: userData?.surname || "",
        DOB: userData?.DOB || "",
        sex: userData?.sex || "",
        address: userData?.address || "",
        city: userData?.city || "",
        postalCode: userData?.postalCode || "",
    })

    // Handler functions
    const uploadAvatar = async (file) => {
        if (uploadingAvatar) {
            return
        }
        setUploadingAvatar(true)
        dispatch(
            authActions.onLogin("account/change-avatar", { avatar: file }, (response) => {
                setUploadingAvatar(false)
            }),
        )
    }

    const updateProfileInfo = async () => {
        if (accountUpdate) {
            return
        }
        setAccountUpdate(true)
        dispatch(
            authActions.onLogin("account/update/info", profileData, (response) => {
                setAccountUpdate(false)
                notify(response?.msg, response?.code)
            }),
        )
    }

    const updatePhoneNumber = async () => {
        if (phoneUpdate) {
            return
        }
        setPhoneUpdate(true)
        dispatch(
            authActions.onLogin("account/update/phone", { phone: phoneNumber }, (response) => {
                setPhoneUpdate(false)
                notify(response?.msg, response?.code)
            }),
        )
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setProfileData((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const getGenderLabel = (value) => {
        if (value === "0") return "Male"
        if (value === "1") return "Female"
        return "Not Specified"
    }

    // Neon glow effect for inputs
    const inputClass =
        "w-full bg-black border-2 border-transparent focus:border-cyan-500 rounded-lg px-4 py-3 focus:outline-none text-black transition-all duration-300 backdrop-blur-sm bg-opacity-30 shadow-[0_0_15px_rgba(0,200,255,0.15)] focus:shadow-[0_0_20px_rgba(0,200,255,0.3)]"

    // Neon button styles
    const neonButtonClass =
        "relative overflow-hidden group py-3 px-6 rounded-lg font-medium transition-all duration-300 shadow-lg"
    const neonPrimaryButton = `${neonButtonClass} bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white`
    const neonSecondaryButton = `${neonButtonClass} bg-black border border-purple-500 text-purple-400 hover:text-white hover:bg-purple-900 hover:bg-opacity-30`

    return (
        <WrapUpP2p>
            <div className="min-h-screen bg-black text-white">
                

                {/* Main content */}
                <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
                    
                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                        <div>
                            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-purple-500 to-fuchsia-500 inline-block">
                                Profile Settings
                            </h1>
                            <p className="text-gray-400 mt-1">Customize your profile and personal information</p>
                        </div>

                        <div className="flex items-center gap-2 px-4 py-2 bg-black border border-purple-800 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                            <UserCircle size={18} className="text-purple-400" />
                            <span className="text-purple-200">{userData?.username}</span>
                        </div>
                    </div>

                    {/* Profile card */}
                    <div className="relative mb-10 rounded-2xl overflow-hidden border border-gray-800 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
                        {/* Profile banner */}
                        <div className="h-48 bg-gradient-to-r from-cyan-900 via-purple-900 to-fuchsia-900 relative overflow-hidden">
                            {/* Animated light effects */}
                            <div className="absolute inset-0">
                                <div className="absolute top-0 left-1/4 w-1/2 h-1 bg-cyan-400 blur-sm animate-pulse"></div>
                                <div
                                    className="absolute bottom-0 right-1/4 w-1/2 h-1 bg-fuchsia-400 blur-sm animate-pulse"
                                    style={{ animationDelay: "1.5s" }}
                                ></div>
                                <div
                                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-purple-500 rounded-full filter blur-[80px] opacity-30 animate-pulse"
                                    style={{ animationDelay: "1s" }}
                                ></div>
                            </div>

                            {/* User info overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
                                <div className="flex items-end gap-6">
                                    {/* Avatar container */}
                                    <div className="relative group">
                                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-black overflow-hidden shadow-[0_0_20px_rgba(139,92,246,0.5)] transition-transform group-hover:scale-105">
                                            <img
                                                className="w-full h-full object-cover"
                                                src={`${guessAdminRoute()}/${userData?.image}`}
                                                alt="Profile"
                                            />

                                            {/* Upload overlay */}
                                            {uploadingAvatar ? (
                                                <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center">
                                                    <div className="animate-spin text-purple-400">
                                                        <Camera size={24} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                    <label htmlFor="avatar-upload" className="cursor-pointer">
                                                        <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center shadow-[0_0_10px_rgba(168,85,247,0.5)]">
                                                            <Camera size={16} className="text-white" />
                                                        </div>
                                                    </label>
                                                    <input
                                                        type="file"
                                                        id="avatar-upload"
                                                        className="hidden"
                                                        onChange={(e) => uploadAvatar(e.target.files[0])}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Glowing ring effect */}
                                        <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-cyan-500 to-purple-600 opacity-0 group-hover:opacity-70 blur-md -z-10 transition-opacity"></div>
                                    </div>

                                    {/* User details */}
                                    <div className="flex-1">
                                        <h2 className="text-2xl font-bold text-white">
                                            {profileData.firstname || userData?.firstname || ""}{" "}
                                            {profileData.surname || userData?.surname || ""}
                                        </h2>
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-1">
                                            <div className="flex items-center text-gray-300">
                                                <AtSign size={14} className="mr-1 text-purple-400" />
                                                <span>{userData?.email}</span>
                                            </div>
                                            <div className="flex items-center text-gray-300">
                                                <Phone size={14} className="mr-1 text-cyan-400" />
                                                <span>{phoneNumber || userData?.phone || "Not set"}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main form content */}
                        <div className="bg-black p-6 md:p-12">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {/* Personal Information */}
                                <div className="md:col-span-3 space-y-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <User size={20} className="text-cyan-400" />
                                        <h3 className="text-xl font-semibold text-white">Personal Information</h3>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-gray-400 text-sm mb-2">First Name</label>
                                            <input
                                                type="text"
                                                name="firstname"
                                                value={profileData.firstname}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                                placeholder="Enter first name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-gray-400 text-sm mb-2">Middle Name</label>
                                            <input
                                                type="text"
                                                name="middlename"
                                                value={profileData.middlename}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                                placeholder="Enter middle name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-gray-400 text-sm mb-2">Last Name</label>
                                            <input
                                                type="text"
                                                name="surname"
                                                value={profileData.surname}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                                placeholder="Enter last name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-gray-400 text-sm mb-2">Gender</label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => handleInputChange({ target: { name: "sex", value: "0" } })}
                                                    className={`relative px-4 py-3 rounded-lg border transition-all duration-300 ${profileData.sex === "0"
                                                            ? "border-cyan-500 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.3)]"
                                                            : "border-gray-800 text-gray-400 hover:border-gray-700"
                                                        }`}
                                                >
                                                    {profileData.sex === "0" && (
                                                        <div className="absolute inset-0 bg-cyan-900 opacity-20 rounded-lg"></div>
                                                    )}
                                                    <span className="relative z-10">Male</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleInputChange({ target: { name: "sex", value: "1" } })}
                                                    className={`relative px-4 py-3 rounded-lg border transition-all duration-300 ${profileData.sex === "1"
                                                            ? "border-fuchsia-500 text-fuchsia-300 shadow-[0_0_15px_rgba(192,38,211,0.3)]"
                                                            : "border-gray-800 text-gray-400 hover:border-gray-700"
                                                        }`}
                                                >
                                                    {profileData.sex === "1" && (
                                                        <div className="absolute inset-0 bg-fuchsia-900 opacity-20 rounded-lg"></div>
                                                    )}
                                                    <span className="relative z-10">Female</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {1>2 && <div className="pt-4 border-t border-gray-800">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Phone size={20} className="text-purple-400" />
                                            <h3 className="text-xl font-semibold text-white">Contact Information</h3>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-gray-400 text-sm mb-2">Phone Number</label>
                                                <div className="flex">
                                                    <span className="inline-flex items-center px-4 bg-gray-900 border-y border-l border-gray-800 rounded-l-lg text-gray-400">
                                                        {userData?.countryPhoneCode || "+1"}
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={phoneNumber}
                                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                                        className="flex-1 bg-black border-y border-r border-gray-800 rounded-r-lg px-4 py-3 focus:outline-none focus:border-purple-500 text-white transition-all duration-300"
                                                        placeholder="Enter phone number"
                                                    />
                                                </div>
                                                <button
                                                    onClick={updatePhoneNumber}
                                                    disabled={phoneUpdate}
                                                    className={`mt-2 px-4 py-2 text-sm ${neonSecondaryButton}`}
                                                >
                                                    {phoneUpdate ? (
                                                        <>
                                                            <div className="animate-spin mr-2 h-4 w-4 border-2 border-purple-400 border-t-transparent rounded-full inline-block"></div>
                                                            Updating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Check className="mr-2 inline-block" size={14} />
                                                            Update Phone
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            <div>
                                                <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                                                <div className="flex items-center">
                                                    <input
                                                        type="email"
                                                        value={userData?.email}
                                                        disabled
                                                        className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-gray-400 cursor-not-allowed"
                                                    />
                                                    <div
                                                        className="ml-2 text-gray-500 cursor-not-allowed"
                                                        title="Contact support to change email"
                                                    >
                                                        <Edit3 size={18} />
                                                    </div>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2">Contact support to change your email address</p>
                                            </div>
                                        </div>
                                    </div>}

                                    <div className="pt-4 border-t border-gray-800">
                                        <div className="flex items-center gap-2 mb-4">
                                            <MapPin size={20} className="text-fuchsia-400" />
                                            <h3 className="text-xl font-semibold text-white">Location Information</h3>
                                        </div>

                                        <div className="grid grid-cols-1 gap-6">
                                            <div>
                                                <label className="block text-gray-400 text-sm mb-2">Address</label>
                                                <input
                                                    type="text"
                                                    name="address"
                                                    value={profileData.address}
                                                    onChange={handleInputChange}
                                                    className={inputClass}
                                                    placeholder="Enter address"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-gray-400 text-sm mb-2">City</label>
                                                    <input
                                                        type="text"
                                                        name="city"
                                                        value={profileData.city}
                                                        onChange={handleInputChange}
                                                        className={inputClass}
                                                        placeholder="Enter city"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-gray-400 text-sm mb-2">Postal Code</label>
                                                    <input
                                                        type="text"
                                                        name="postalCode"
                                                        value={profileData.postalCode}
                                                        onChange={handleInputChange}
                                                        className={inputClass}
                                                        placeholder="Enter postal code"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Profile Summary */}
                                {1>2 && <div className="space-y-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Sparkles size={20} className="text-purple-400" />
                                        <h3 className="text-xl font-semibold text-white">Profile Summary</h3>
                                    </div>

                                    {/* Profile card */}
                                    <div className="relative rounded-xl overflow-hidden">
                                        {/* Animated border */}
                                        <div className="absolute -inset-[1px] bg-gradient-to-r from-cyan-500 via-purple-500 to-fuchsia-500 rounded-xl animate-gradient-x"></div>

                                        <div className="relative bg-black p-5 rounded-xl">
                                            <div className="flex items-center gap-4 mb-4">
                                                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                                                    <img
                                                        className="w-full h-full object-cover"
                                                        src={`${guessAdminRoute()}/${userData?.image}`}
                                                        alt="Profile"
                                                    />
                                                </div>
                                                <div>
                                                    <h4 className="text-lg font-medium text-white">{userData?.username}</h4>
                                                    <p className="text-gray-400 text-sm">{userData?.email}</p>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-900 bg-opacity-50">
                                                    <User className="text-cyan-400" size={18} />
                                                    <div>
                                                        <div className="text-sm text-gray-400">Full Name</div>
                                                        <div className="text-white">
                                                            {profileData.firstname || userData?.firstname || "Not set"}{" "}
                                                            {profileData.surname || userData?.surname || ""}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-900 bg-opacity-50">
                                                    <Phone className="text-purple-400" size={18} />
                                                    <div>
                                                        <div className="text-sm text-gray-400">Phone</div>
                                                        <div className="text-white">{phoneNumber || userData?.phone || "Not set"}</div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-900 bg-opacity-50">
                                                    <MapPin className="text-fuchsia-400" size={18} />
                                                    <div>
                                                        <div className="text-sm text-gray-400">Location</div>
                                                        <div className="text-white">
                                                            {profileData.city || userData?.city || "Not set"}
                                                            {(profileData.city || userData?.city) &&
                                                                profileData.postalCode &&
                                                                `, ${profileData.postalCode}`}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status indicator */}
                                    <div className="p-4 rounded-lg bg-gradient-to-r from-purple-900/30 to-fuchsia-900/30 border border-purple-800/50">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
                                            <span className="text-green-400 font-medium">Active Account</span>
                                        </div>
                                        <p className="text-gray-400 text-sm mt-2">Your profile is visible to others in the network</p>
                                    </div>
                                </div>}

                            </div>

                            {/* Save button */}
                            <div className="mt-8">
                                <button onClick={updateProfileInfo} disabled={accountUpdate} className={`w-full ${neonPrimaryButton}`}>
                                    {/* Animated background effect */}
                                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-cyan-600/0 via-purple-600/30 to-fuchsia-600/0 group-hover:via-purple-600/40 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-out"></span>

                                    <span className="relative flex items-center justify-center text-lg">
                                        {accountUpdate ? (
                                            <>
                                                <div className="animate-spin mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                                Updating Profile...
                                            </>
                                        ) : (
                                            <>
                                                <Zap className="mr-2" size={20} />
                                                Save All Changes
                                            </>
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </WrapUpP2p>
    )
}

export default SettingsProfile
